import axios from 'axios';
import { STORAGE_KEYS, API_ENDPOINTS } from '../utils/constants';
import { getStorageItem, removeStorageItem } from '../utils/helpers';
import tokenService from './tokenService';

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://api.teonila.com',
  timeout: 10000,
  withCredentials: true,  // Incluir cookies en todas las peticiones (HttpOnly cookies)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // Con HttpOnly cookies, los tokens se envían automáticamente
    // No es necesario añadir el Authorization header manualmente
    // El backend ahora lee el token desde las cookies

    // NOTA: Si necesitas retrocompatibilidad temporal con el método anterior,
    // descomenta las siguientes líneas:
    // const token = tokenService.getAccessToken();
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const errorDetail = error.response?.data?.detail;
    const originalRequest = error.config;

    // Handle network errors or server unreachable
    if (!error.response) {
      // Check if the user is offline (no internet connection)
      const isOffline = !navigator.onLine;

      // Only show maintenance screen if it's NOT an offline situation
      // If offline, the OfflineIndicator will handle the UI
      if (!isOffline) {
        // Network error or server is actually down
        window.dispatchEvent(new CustomEvent('server-maintenance', {
          detail: { error: error.message }
        }));
      }

      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      // Verificar primero si estamos en una ruta pública
      const publicRoutes = ['/', '/login', '/privacy', '/payment-success', '/payment-cancel'];
      const isPublicRoute = publicRoutes.includes(window.location.pathname);

      // Lista de endpoints completamente públicos que nunca necesitan autenticación
      const publicEndpoints = [
        '/auth/login',
        '/subscriptions/plans',
        '/contact/demo-request'
      ];

      // Verificar si el request es a un endpoint completamente público
      const isPublicEndpoint = publicEndpoints.some(endpoint =>
        originalRequest.url.includes(endpoint)
      );

      // Si estamos en ruta pública O es un endpoint público, no intentar refresh
      if (isPublicRoute || isPublicEndpoint) {
        // Solo limpiar tokens si no estamos intentando login
        if (!originalRequest.url.includes('/auth/login')) {
          tokenService.clearTokens();
        }
        return Promise.reject(error);
      }

      // Mark request as retried to avoid infinite loop
      originalRequest._retry = true;

      // Skip refresh for refresh endpoint to avoid loop
      if (originalRequest.url.includes('/auth/refresh')) {
        tokenService.clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Con HttpOnly cookies, no podemos verificar si hay un refresh token
      // Simplemente intentamos refrescar (el backend lee el token desde cookies)
      try {
        return await tokenService.handleTokenRefresh(originalRequest);
      } catch (refreshError) {
        // Refresh failed
        tokenService.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 400 Bad Request - puede ser refresh token faltante o inválido
    if (status === 400) {
      // Si es en el endpoint de refresh y dice que falta el token, NO redirigir
      // Solo limpiar tokens y rechazar el error. El componente decidirá qué hacer.
      if (originalRequest.url.includes('/auth/refresh') &&
          (errorDetail?.includes('Refresh token is required') || errorDetail?.includes('required'))) {
        tokenService.clearTokens();
        // No redirigir aquí - dejar que el flujo normal maneje esto
        return Promise.reject(error);
      }

      // Handle inactive user
      if (errorDetail && (errorDetail.includes('Inactive') || errorDetail.includes('inactivo'))) {
        // Clear auth data
        removeStorageItem(STORAGE_KEYS.TOKEN);
        removeStorageItem(STORAGE_KEYS.USER);

        // No redirigir si estamos en rutas públicas
        const publicRoutes = ['/', '/login', '/privacy', '/payment-success', '/payment-cancel'];
        const isPublicRoute = publicRoutes.includes(window.location.pathname);

        if (!isPublicRoute) {
          // Dispatch custom event for showing notification
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: { message: 'Tu cuenta ha sido desactivada. Por favor, contacta con el administrador.' }
          }));

          window.location.href = '/login';
        }
      }
    }

    // Handle 403 Forbidden (expired account, inactive user)
    if (status === 403) {
      // Check if it's an expired account
      if (errorDetail && (errorDetail.includes('expired') || errorDetail.includes('expirado'))) {
        // Clear auth data
        removeStorageItem(STORAGE_KEYS.TOKEN);
        removeStorageItem(STORAGE_KEYS.USER);

        // No redirigir si estamos en rutas públicas
        const publicRoutes = ['/', '/login', '/privacy', '/payment-success', '/payment-cancel'];
        const isPublicRoute = publicRoutes.includes(window.location.pathname);

        if (!isPublicRoute) {
          // Dispatch custom event for showing notification
          window.dispatchEvent(new CustomEvent('auth-error', {
            detail: { message: 'Tu cuenta ha expirado. Por favor, contacta con el administrador.' }
          }));

          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    // El backend espera form data, no JSON
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  logout: async () => {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGOUT);
    return response.data;
  },

  me: async (trackLogin = false) => {
    const response = await api.get(API_ENDPOINTS.AUTH.ME, {
      params: { track_login: trackLogin }
    });
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.ORDERS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.ORDERS}${id}`);
    return response.data;
  },

  create: async (orderData) => {
    const response = await api.post(API_ENDPOINTS.ORDERS, orderData);
    return response.data;
  },

  update: async (id, orderData) => {
    const response = await api.put(`${API_ENDPOINTS.ORDERS}${id}`, orderData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ORDERS}${id}`);
    return response.data;
  },

  complete: async (id) => {
    const response = await api.patch(`${API_ENDPOINTS.ORDERS}${id}/mark-cleaning`);
    return response.data;
  },

  unmark: async (id) => {
    const response = await api.patch(`${API_ENDPOINTS.ORDERS}${id}/unmark-cleaning`);
    return response.data;
  },

  markCheckout: async (id) => {
    const response = await api.patch(`${API_ENDPOINTS.ORDERS}${id}/mark-checkout`);
    return response.data;
  },

  unmarkCheckout: async (id) => {
    const response = await api.patch(`${API_ENDPOINTS.ORDERS}${id}/unmark-checkout`);
    return response.data;
  },

  // Legacy method for backward compatibility
  updateCheckout: async (id, isCompleted) => {
    if (isCompleted) {
      return api.patch(`${API_ENDPOINTS.ORDERS}${id}/mark-checkout`).then(r => r.data);
    } else {
      return api.patch(`${API_ENDPOINTS.ORDERS}${id}/unmark-checkout`).then(r => r.data);
    }
  },


  bulkCreate: async (orders) => {
    const response = await api.post(`${API_ENDPOINTS.ORDERS}bulk`, { orders });
    return response.data;
  },

  getByDate: async (date) => {
    const response = await api.get(API_ENDPOINTS.ORDERS, {
      params: { date, limit: 1000 }
    });
    return response.data;
  },

  getMyOrders: async (date) => {
    const response = await api.get(`${API_ENDPOINTS.ORDERS}my-orders`, {
      params: { date }
    });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.USERS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.USERS}${id}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await api.post(API_ENDPOINTS.USERS, userData);
    return response.data;
  },

  update: async (id, userData) => {
    const response = await api.put(`${API_ENDPOINTS.USERS}${id}`, userData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.USERS}${id}`);
    return response.data;
  },

  getCleaningStaff: async () => {
    const response = await api.get(`${API_ENDPOINTS.USERS}cleaning-staff`);
    return response.data;
  },

  setExpiration: async (id, expirationDate) => {
    const response = await api.put(`${API_ENDPOINTS.USERS}${id}/expiration`, {
      expiration_date: expirationDate
    });
    return response.data;
  },

  removeExpiration: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.USERS}${id}/expiration`);
    return response.data;
  },

  changePassword: async (id, newPassword) => {
    const response = await api.put(`${API_ENDPOINTS.USERS}${id}/password`, {
      password: newPassword
    });
    return response.data;
  },
};

// Rooms API
export const roomsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.ROOMS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.ROOMS}${id}`);
    return response.data;
  },

  create: async (roomData) => {
    const response = await api.post(API_ENDPOINTS.ROOMS, roomData);
    return response.data;
  },

  update: async (id, roomData) => {
    const response = await api.put(`${API_ENDPOINTS.ROOMS}${id}`, roomData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ROOMS}${id}`);
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`${API_ENDPOINTS.ROOMS}${id}/status`, { status });
    return response.data;
  },

  getHistory: async (roomId) => {
    const response = await api.get(`${API_ENDPOINTS.ORDERS}room/${roomId}/history`);
    return response.data;
  },
};

// Room Groups API
export const roomGroupsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.ROOM_GROUPS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.ROOM_GROUPS}${id}`);
    return response.data;
  },

  create: async (groupData) => {
    const response = await api.post(API_ENDPOINTS.ROOM_GROUPS, groupData);
    return response.data;
  },

  update: async (id, groupData) => {
    const response = await api.put(`${API_ENDPOINTS.ROOM_GROUPS}${id}`, groupData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ROOM_GROUPS}${id}`);
    return response.data;
  },

  updateOrder: async (id, newOrder) => {
    const response = await api.put(`${API_ENDPOINTS.ROOM_GROUPS}${id}/order`, null, {
      params: { new_order: newOrder }
    });
    return response.data;
  },
};

// Common Spaces API
export const commonSpacesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.COMMON_SPACES, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.COMMON_SPACES}${id}`);
    return response.data;
  },

  create: async (spaceData) => {
    const response = await api.post(API_ENDPOINTS.COMMON_SPACES, spaceData);
    return response.data;
  },

  update: async (id, spaceData) => {
    const response = await api.put(`${API_ENDPOINTS.COMMON_SPACES}${id}`, spaceData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.COMMON_SPACES}${id}`);
    return response.data;
  },

  getActiveByEstablishment: async (establishmentId, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.COMMON_SPACES}establishment/${establishmentId}/active`, { params });
    return response.data;
  },
};

// Establishments API
export const establishmentsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.ESTABLISHMENTS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.ESTABLISHMENTS}${id}`);
    return response.data;
  },

  create: async (establishmentData) => {
    const response = await api.post(API_ENDPOINTS.ESTABLISHMENTS, establishmentData);
    return response.data;
  },

  update: async (id, establishmentData) => {
    const response = await api.put(`${API_ENDPOINTS.ESTABLISHMENTS}${id}`, establishmentData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ESTABLISHMENTS}${id}`);
    return response.data;
  },
};

// Establishment Addresses API
export const establishmentAddressesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.ESTABLISHMENT_ADDRESSES, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.ESTABLISHMENT_ADDRESSES}${id}`);
    return response.data;
  },

  create: async (addressData) => {
    const response = await api.post(API_ENDPOINTS.ESTABLISHMENT_ADDRESSES, addressData);
    return response.data;
  },

  update: async (id, addressData) => {
    const response = await api.put(`${API_ENDPOINTS.ESTABLISHMENT_ADDRESSES}${id}`, addressData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.ESTABLISHMENT_ADDRESSES}${id}`);
    return response.data;
  },

  setPrimary: async (id) => {
    const response = await api.post(`${API_ENDPOINTS.ESTABLISHMENT_ADDRESSES}${id}/set-primary`);
    return response.data;
  },
};

// Incidents API
export const incidentsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.INCIDENTS, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.INCIDENTS}${id}`);
    return response.data;
  },

  getByRoom: async (roomId) => {
    const response = await api.get(`${API_ENDPOINTS.INCIDENTS}room/${roomId}`);
    return response.data;
  },

  create: async (incidentData) => {
    const response = await api.post(API_ENDPOINTS.INCIDENTS, incidentData);
    return response.data;
  },

  update: async (id, incidentData) => {
    const response = await api.put(`${API_ENDPOINTS.INCIDENTS}${id}`, incidentData);
    return response.data;
  },

  close: async (id) => {
    const response = await api.post(`${API_ENDPOINTS.INCIDENTS}${id}/close`);
    return response.data;
  },

  reopen: async (id) => {
    const response = await api.post(`${API_ENDPOINTS.INCIDENTS}${id}/reopen`);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.INCIDENTS}${id}`);
    return response.data;
  },
};

// Cleaning Types API
export const cleaningTypesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.CLEANING_TYPES, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.CLEANING_TYPES}${id}`);
    return response.data;
  },

  create: async (cleaningTypeData) => {
    const response = await api.post(API_ENDPOINTS.CLEANING_TYPES, cleaningTypeData);
    return response.data;
  },

  update: async (id, cleaningTypeData) => {
    const response = await api.put(`${API_ENDPOINTS.CLEANING_TYPES}${id}`, cleaningTypeData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.CLEANING_TYPES}${id}`);
    return response.data;
  },

  reorder: async (items) => {
    const response = await api.post(`${API_ENDPOINTS.CLEANING_TYPES}reorder`, items);
    return response.data;
  },
};

// Bed Dispositions API
export const bedDispositionsAPI = {
  getAll: async () => {
    const response = await api.get(API_ENDPOINTS.BED_DISPOSITIONS);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.BED_DISPOSITIONS}${id}`);
    return response.data;
  },

  create: async (bedDispositionData) => {
    const response = await api.post(API_ENDPOINTS.BED_DISPOSITIONS, bedDispositionData);
    return response.data;
  },

  update: async (id, bedDispositionData) => {
    const response = await api.put(`${API_ENDPOINTS.BED_DISPOSITIONS}${id}`, bedDispositionData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.BED_DISPOSITIONS}${id}`);
    return response.data;
  },

  reorder: async (items) => {
    const response = await api.post(`${API_ENDPOINTS.BED_DISPOSITIONS}reorder`, items);
    return response.data;
  },
};


// Login History API
export const loginHistoryAPI = {
  // Get my last login
  getMyLastLogin: async () => {
    const response = await api.get(`${API_ENDPOINTS.LOGIN_HISTORY}my-last-login`);
    return response.data;
  },

  // Get all login history (platform admin only)
  getAll: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.LOGIN_HISTORY}all`, { params });
    return response.data;
  },

  // Get login history for a specific user (platform admin only)
  getByUser: async (userId, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.LOGIN_HISTORY}user/${userId}`, { params });
    return response.data;
  },

  // Get login count for a specific user (platform admin only)
  getUserLoginCount: async (userId) => {
    const response = await api.get(`${API_ENDPOINTS.LOGIN_HISTORY}user/${userId}/count`);
    return response.data;
  },

  // Get login history for all users in an establishment (platform admin only)
  getByEstablishment: async (establishmentId, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.LOGIN_HISTORY}establishment/${establishmentId}`, { params });
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsAPI = {
  // Planes - Público
  getPlans: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}plans`, { params });
    return response.data;
  },

  getPlanById: async (planId) => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}plans/${planId}`);
    return response.data;
  },

  // Planes - Admin plataforma
  createPlan: async (planData) => {
    const response = await api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}plans`, planData);
    return response.data;
  },

  updatePlan: async (planId, planData) => {
    const response = await api.put(`${API_ENDPOINTS.SUBSCRIPTIONS}plans/${planId}`, planData);
    return response.data;
  },

  deletePlan: async (planId) => {
    const response = await api.delete(`${API_ENDPOINTS.SUBSCRIPTIONS}plans/${planId}`);
    return response.data;
  },

  // Suscripciones - Admin plataforma
  createSubscription: async (subscriptionData) => {
    const response = await api.post(API_ENDPOINTS.SUBSCRIPTIONS, subscriptionData);
    return response.data;
  },

  assignTrial: async (trialData) => {
    const response = await api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}assign-trial`, trialData);
    return response.data;
  },

  assignSubscription: async (subscriptionData) => {
    const response = await api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}assign-subscription`, subscriptionData);
    return response.data;
  },

  getAllSubscriptions: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.SUBSCRIPTIONS, { params });
    return response.data;
  },

  // Estado de suscripción - Usuario actual
  getMyStatus: async () => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}status`);
    return response.data;
  },

  // Historial de suscripciones - Usuario actual
  getMyHistory: async () => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}history`);
    return response.data;
  },

  // Cancelar suscripción
  cancelSubscription: async (subscriptionId, cancelData) => {
    const response = await api.post(
      `${API_ENDPOINTS.SUBSCRIPTIONS}${subscriptionId}/cancel`,
      cancelData
    );
    return response.data;
  },

  // Reactivar suscripción - Admin plataforma
  reactivateSubscription: async (subscriptionId) => {
    const response = await api.post(
      `${API_ENDPOINTS.SUBSCRIPTIONS}${subscriptionId}/reactivate`
    );
    return response.data;
  },

  // Estadísticas - Admin plataforma
  getStats: async () => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}stats`);
    return response.data;
  },

  // PayPal Integration
  createCheckout: async (checkoutData) => {
    const response = await api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}checkout`, checkoutData);
    return response.data;
  },

  renewCheckout: async (plan, period) => {
    const response = await api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}renew-checkout`, null, {
      params: { plan, period }
    });
    return response.data;
  },

  capturePayment: async (paymentId) => {
    const response = await api.post(
      `${API_ENDPOINTS.SUBSCRIPTIONS}capture-payment/${paymentId}`
    );
    return response.data;
  },

  // Features
  getPlanFeatures: async (planId) => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}plans/${planId}/features`);
    return response.data;
  },

  updatePlanFeatures: async (planId, featuresData) => {
    const response = await api.put(`${API_ENDPOINTS.SUBSCRIPTIONS}plans/${planId}/features`, featuresData);
    return response.data;
  },

  getCurrentFeatures: async () => {
    const response = await api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}current/features`);
    return response.data;
  },
};

// Work Schedules API
export const workSchedulesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.WORK_SCHEDULES, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.WORK_SCHEDULES}${id}`);
    return response.data;
  },

  getByMonth: async (year, month, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.WORK_SCHEDULES}month/${year}/${month}`, { params });
    return response.data;
  },

  create: async (scheduleData) => {
    const response = await api.post(API_ENDPOINTS.WORK_SCHEDULES, scheduleData);
    return response.data;
  },

  bulkCreateOrUpdate: async (schedules) => {
    const response = await api.post(`${API_ENDPOINTS.WORK_SCHEDULES}bulk`, { schedules });
    return response.data;
  },

  update: async (id, scheduleData) => {
    const response = await api.put(`${API_ENDPOINTS.WORK_SCHEDULES}${id}`, scheduleData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.WORK_SCHEDULES}${id}`);
    return response.data;
  },

  downloadPDF: async (year, month) => {
    const response = await api.get(`${API_ENDPOINTS.WORK_SCHEDULES}pdf/${year}/${month}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getColorSchedule: async (year, month, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.WORK_SCHEDULES}colors/${year}/${month}`, { params });
    return response.data;
  },
};

// Schedule Special Statuses API
export const scheduleSpecialStatusesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.SCHEDULE_SPECIAL_STATUSES, { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`${API_ENDPOINTS.SCHEDULE_SPECIAL_STATUSES}${id}`);
    return response.data;
  },

  create: async (statusData) => {
    const response = await api.post(API_ENDPOINTS.SCHEDULE_SPECIAL_STATUSES, statusData);
    return response.data;
  },

  update: async (id, statusData) => {
    const response = await api.put(`${API_ENDPOINTS.SCHEDULE_SPECIAL_STATUSES}${id}`, statusData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.SCHEDULE_SPECIAL_STATUSES}${id}`);
    return response.data;
  },
};

// Reports API
export const reportsAPI = {
  getSummary: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}summary`, { params });
    return response.data;
  },

  getStaffPerformance: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}staff-performance`, { params });
    return response.data;
  },

  getIncidentsSummary: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}incidents`, { params });
    return response.data;
  },

  getStaffWorkload: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}staff/workload`, { params });
    return response.data;
  },

  getStaffExpenses: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}staff/expenses`, { params });
    return response.data;
  },

  // Advanced Reports
  getCommonSpacesAnalytics: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}common-spaces/analytics`, { params });
    return response.data;
  },

  getRoomProfitability: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}profitability/rooms`, { params });
    return response.data;
  },

  getScheduleCompliance: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}schedule-compliance`, { params });
    return response.data;
  },

  getAdvancedProductivity: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}productivity/advanced`, { params });
    return response.data;
  },

  getFinancialDashboard: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}financial/dashboard`, { params });
    return response.data;
  },

  getQualityDashboard: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}quality/dashboard`, { params });
    return response.data;
  },

  // Admin-specific Reports
  getRealtimeDashboard: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}admin/realtime-dashboard`, { params });
    return response.data;
  },

  getComparativeAnalysis: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}admin/comparative-analysis`, { params });
    return response.data;
  },

  getPriorityManagement: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}admin/priority-management`, { params });
    return response.data;
  },

  getLaundryCostsDaily: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}laundry-costs-daily`, { params });
    return response.data;
  },

  getStaffAvgCleaningTime: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}staff/avg-cleaning-time`, { params });
    return response.data;
  },

  getRoomCosts: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}room-costs`, { params });
    return response.data;
  },

  getFilters: async () => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}filters`);
    return response.data;
  },

  getDashboardSummary: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.REPORTS}dashboard-summary`, { params });
    return response.data;
  },
};

// Time Entries API
export const timeEntriesAPI = {
  getActive: async () => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRIES}active`);
    return response.data;
  },

  getTodayTotal: async () => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRIES}today-total`);
    return response.data;
  },

  clockIn: async (location = null) => {
    const data = {};
    if (location) {
      data.latitude = location.latitude;
      data.longitude = location.longitude;
    }
    const response = await api.post(`${API_ENDPOINTS.TIME_ENTRIES}clock-in`, data);
    return response.data;
  },

  clockOut: async (notes = null, location = null) => {
    const data = { notes };
    if (location) {
      data.latitude = location.latitude;
      data.longitude = location.longitude;
    }
    const response = await api.post(`${API_ENDPOINTS.TIME_ENTRIES}clock-out`, data);
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.TIME_ENTRIES, { params });
    return response.data;
  },

  getByMonth: async (year, month, params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRIES}month/${year}/${month}`, { params });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`${API_ENDPOINTS.TIME_ENTRIES}${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`${API_ENDPOINTS.TIME_ENTRIES}${id}`);
    return response.data;
  },

  exportPdf: async (userId, startDate, endDate) => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRIES}export/pdf`, {
      params: {
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Time Entry Modifications API
export const timeEntryModificationsAPI = {
  create: async (data) => {
    const response = await api.post(API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS, data);
    return response.data;
  },

  getPending: async () => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS}pending`);
    return response.data;
  },

  getPendingCount: async () => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS}pending/count`);
    return response.data;
  },

  getMyRequests: async () => {
    const response = await api.get(`${API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS}my-requests`);
    return response.data;
  },

  review: async (requestId, data) => {
    const response = await api.post(`${API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS}${requestId}/review`, data);
    return response.data;
  },

  cancel: async (requestId) => {
    const response = await api.delete(`${API_ENDPOINTS.TIME_ENTRY_MODIFICATIONS}${requestId}`);
    return response.data;
  },
};

// Month Validations API
export const monthValidationsAPI = {
  validate: async (data) => {
    const response = await api.post(API_ENDPOINTS.MONTH_VALIDATIONS, data);
    return response.data;
  },

  validateBulk: async (data) => {
    const response = await api.post(`${API_ENDPOINTS.MONTH_VALIDATIONS}bulk`, data);
    return response.data;
  },

  getStatus: async (year, month) => {
    const response = await api.get(`${API_ENDPOINTS.MONTH_VALIDATIONS}status/${year}/${month}`);
    return response.data;
  },

  checkValidated: async (userId, year, month) => {
    const response = await api.get(`${API_ENDPOINTS.MONTH_VALIDATIONS}check`, {
      params: { user_id: userId, year, month }
    });
    return response.data;
  },

  invalidate: async (validationId) => {
    const response = await api.delete(`${API_ENDPOINTS.MONTH_VALIDATIONS}${validationId}`);
    return response.data;
  },
};

// Demo Requests API
export const demoRequestsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get(`${API_ENDPOINTS.CONTACT}demo-requests`, { params });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.patch(`${API_ENDPOINTS.CONTACT}demo-requests/${id}`, data);
    return response.data;
  },
};

export default api;